import React, { useState, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import {
    BlockRounded,
    KeyboardArrowDownRounded,
    SearchRounded,
    CloseRounded,
    RestartAltRounded,
    CheckCircleOutlineRounded,
} from "@mui/icons-material";
import { getUnifiedSportsList } from "../../../helper/constants/sportsConstants";

const _BlockedSportsSelector = ({
    usageCategories,
    blockedSportIds,
    setBlockedSportIds,
    sportsList = [],
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterOnlyBlocked, setFilterOnlyBlocked] = useState(false);

    // ادغام لیست ورزش‌های API با ورزش‌های استاندارد تا همواره تمامی رشته‌ها در دسترس باشند
    const allSports = useMemo(() => {
        return getUnifiedSportsList(sportsList);
    }, [sportsList]);

    // فیلتر جستجو و وضعیت
    const filteredSports = useMemo(() => {
        let list = allSports;
        if (filterOnlyBlocked) {
            list = list.filter(s => blockedSportIds.some(id => String(id) === String(s.Id)));
        }
        if (searchQuery.trim()) {
            const query = searchQuery.trim().toLowerCase();
            list = list.filter(s => s.Name.toLowerCase().includes(query));
        }
        return list;
    }, [allSports, searchQuery, filterOnlyBlocked, blockedSportIds]);

    if (!usageCategories.includes("SPORT")) return null;

    const handleToggleSport = (sportId) => {
        const idStr = String(sportId);
        const exists = blockedSportIds.some(id => String(id) === idStr);
        if (exists) {
            setBlockedSportIds(blockedSportIds.filter(id => String(id) !== idStr));
        } else {
            setBlockedSportIds([...blockedSportIds, sportId]);
        }
    };

    const handleClearAllBlocked = () => {
        setBlockedSportIds([]);
    };

    const isBlocked = (sportId) => {
        return blockedSportIds.some(id => String(id) === String(sportId));
    };

    return (
        <Box
            sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: open ? (blockedSportIds.length > 0 ? 'error.main' : 'primary.main') : 'divider',
                bgcolor: 'action.hover',
                overflow: 'hidden',
                transition: 'all 0.25s ease',
            }}
        >
            {/* Clickable Header Bar */}
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        bgcolor: 'action.selected',
                    },
                    transition: 'background-color 0.2s',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <BlockRounded color={blockedSportIds.length > 0 ? "error" : "action"} sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        ورزش‌های غیرمجاز (اختیاری):
                    </Typography>
                    <Chip
                        size="small"
                        label={
                            blockedSportIds.length === 0
                                ? "همه مجاز (بدون محدودیت)"
                                : `${blockedSportIds.length} ورزش محدود شده`
                        }
                        color={blockedSportIds.length > 0 ? "error" : "default"}
                        variant={blockedSportIds.length > 0 ? "filled" : "outlined"}
                        sx={{ fontWeight: 700, height: 24, fontSize: '0.75rem' }}
                    />
                </Box>

                <IconButton
                    size="small"
                    sx={{
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
                        color: open ? 'primary.main' : 'text.secondary',
                    }}
                >
                    <KeyboardArrowDownRounded />
                </IconButton>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={open} timeout="auto" unmountOnExit={false}>
                <Box
                    sx={{
                        px: 2.5,
                        pb: 2.5,
                        pt: 1.5,
                        borderTop: '1px dashed',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        ورزش‌های انتخاب‌شده در این بخش، با این اعتبار توسط پرسنل قابل پرداخت نخواهند بود (برای ایجاد محدودیت روی یک ورزش، روی آن کلیک کنید):
                    </Typography>

                    {/* Search and Quick Filters Bar */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.8, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="جستجوی رشته ورزشی (شنا، پدل، بدنسازی، فوتبال...)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                flex: 1,
                                minWidth: 220,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2.5,
                                    bgcolor: 'background.paper',
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchQuery("")}>
                                            <CloseRounded fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />

                        {/* Filter Tabs */}
                        <Chip
                            label={`همه رشته‌ها (${allSports.length})`}
                            size="small"
                            variant={!filterOnlyBlocked ? "filled" : "outlined"}
                            color={!filterOnlyBlocked ? "primary" : "default"}
                            onClick={() => setFilterOnlyBlocked(false)}
                            sx={{ fontWeight: 700, cursor: 'pointer' }}
                        />

                        {blockedSportIds.length > 0 && (
                            <Chip
                                label={`فقط مسدودها (${blockedSportIds.length})`}
                                size="small"
                                variant={filterOnlyBlocked ? "filled" : "outlined"}
                                color="error"
                                onClick={() => setFilterOnlyBlocked(!filterOnlyBlocked)}
                                sx={{ fontWeight: 700, cursor: 'pointer' }}
                            />
                        )}

                        {blockedSportIds.length > 0 && (
                            <Button
                                size="small"
                                color="inherit"
                                variant="outlined"
                                startIcon={<RestartAltRounded fontSize="small" />}
                                onClick={handleClearAllBlocked}
                                sx={{
                                    borderRadius: 2,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    py: 0.5,
                                    px: 1,
                                    ml: 'auto',
                                }}
                            >
                                مجاز کردن همه
                            </Button>
                        )}
                    </Box>

                    {/* Scrollable Sports Selection Container */}
                    <Box
                        sx={{
                            maxHeight: 240,
                            overflowY: 'auto',
                            p: 1.5,
                            borderRadius: 2.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignContent: 'flex-start',
                            '&::-webkit-scrollbar': {
                                width: 6,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'divider',
                                borderRadius: 3,
                            },
                        }}
                    >
                        {filteredSports.length === 0 ? (
                            <Box sx={{ width: '100%', py: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    هیچ رشته ورزشی با عنوان «{searchQuery}» یافت نشد.
                                </Typography>
                            </Box>
                        ) : (
                            filteredSports.map((sport) => {
                                const blocked = isBlocked(sport.Id);
                                return (
                                    <Tooltip
                                        key={sport.Id}
                                        title={blocked ? "کلیک برای مجاز کردن این ورزش" : "کلیک برای ممنوع کردن این ورزش با این اعتبار"}
                                        arrow
                                    >
                                        <Chip
                                            label={blocked ? `ممنوع: ${sport.Name}` : sport.Name}
                                            clickable
                                            onClick={() => handleToggleSport(sport.Id)}
                                            onDelete={blocked ? () => handleToggleSport(sport.Id) : undefined}
                                            deleteIcon={blocked ? <CloseRounded sx={{ fontSize: '15px !important' }} /> : undefined}
                                            icon={
                                                blocked ? (
                                                    <BlockRounded sx={{ fontSize: '15px !important', color: 'inherit !important' }} />
                                                ) : undefined
                                            }
                                            color={blocked ? "error" : "default"}
                                            variant={blocked ? "filled" : "outlined"}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                py: 2,
                                                px: 0.5,
                                                borderRadius: 2,
                                                borderWidth: blocked ? 1.5 : 1,
                                                borderColor: blocked ? 'error.main' : 'divider',
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    borderColor: blocked ? 'error.dark' : 'error.main',
                                                    color: blocked ? '#fff' : 'error.main',
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })
                        )}
                    </Box>

                    {/* Footer Warning & Guidance */}
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {blockedSportIds.length === 0
                                ? "💡 در حال حاضر تمامی رشته‌های ورزشی مجاز هستند."
                                : `⚠️ ${blockedSportIds.length} رشته ورزشی برای این بسته اعتباری غیرمجاز شد.`}
                        </Typography>

                        {blockedSportIds.length > 0 && (
                            <Alert severity="warning" sx={{ width: '100%', mt: 0.8, py: 0.3, px: 1.5, borderRadius: 2 }}>
                                <Typography variant="caption" sx={{ lineHeight: 1.6, fontWeight: 600 }}>
                                    ⚠️ <strong>قانون بلیط‌های ترکیبی:</strong> اگر بلیطی در سبد خرید پرسنل شامل چند ورزش باشد و حتی <strong>یکی</strong> از آنها در لیست غیرمجاز باشد، امکان پرداخت کل بلیط با این اعتبار وجود نخواهد داشت.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default _BlockedSportsSelector;
